import type { ProviderOut } from "@/types/providers";
import type {
  SchemaColumn,
  SchemaDataType,
  SchemaRule,
  SchemaRulePreset,
  SchemaSummary,
} from "@/types/schemaBuilder";

/** Provider identity needed for Schema Manager PSA pills. */
export interface SchemaAssignedProvider {
  providerId: string;
  shortCode: string;
  name: string;
}

// ── Date formatting ──────────────────────────────────────────

/** "13 June 2026" — used for schema createdAt / lastEditedAt */
export function formatSchemaDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/** Activity-feed dates: time only if today ("3:45 PM"), otherwise "June 13" */
export function formatEventDate(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  }
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

/** Upload session timestamps: "Jun 13, 2026  3:45 pm" */
export function formatUploadDate(iso: string): string {
  const date = new Date(iso);
  const d = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const t = date
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
    .toLowerCase();
  return `${d}  ${t}`;
}

// ── Schema naming ─────────────────────────────────────────────

/** True if `name` matches an existing schema's name (trimmed, case-insensitive). */
export function isSchemaNameTaken(
  name: string,
  existingSchemas: Pick<SchemaSummary, "id" | "name">[],
  excludeSchemaId?: string
): boolean {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return false;
  return existingSchemas.some(
    (schema) => schema.id !== excludeSchemaId && schema.name.trim().toLowerCase() === normalized
  );
}

/** fieldIds of columns whose (trimmed, case-insensitive) name collides with another column's name. */
export function findDuplicateNameColumnIds(
  columns: Pick<SchemaColumn, "fieldId" | "name">[]
): Set<string> {
  const countByName = new Map<string, number>();
  for (const col of columns) {
    const key = col.name.trim().toLowerCase();
    if (!key) continue;
    countByName.set(key, (countByName.get(key) ?? 0) + 1);
  }
  return new Set(
    columns
      .filter((col) => (countByName.get(col.name.trim().toLowerCase()) ?? 0) > 1)
      .map((col) => col.fieldId)
  );
}

/** "Copy of <name>", or "Copy of <name> (2)", "(3)", etc. if that name is already taken. */
export function getNextCopyName(
  sourceName: string,
  existingSchemas: Pick<SchemaSummary, "name">[]
): string {
  const existingNames = new Set(existingSchemas.map((s) => s.name.trim().toLowerCase()));
  const base = `Copy of ${sourceName}`;
  if (!existingNames.has(base.toLowerCase())) return base;

  let n = 2;
  while (existingNames.has(`${base} (${n})`.toLowerCase())) n++;
  return `${base} (${n})`;
}

/** Turns "claims_export-final.xlsx" into "claims export final" for use as a default schema name. */
export function deriveSchemaNameFromFileName(fileName: string): string {
  return fileName
    .replace(/\.[^./\\]+$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** "<templateName> Custom v<n>" with n one higher than the highest version already in use. */
export function getNextTemplateVersionName(
  templateName: string,
  existingSchemas: Pick<SchemaSummary, "name">[]
): string {
  const pattern = new RegExp(`^${escapeRegExp(templateName)} Custom v(\\d+)$`, "i");
  const usedVersions = existingSchemas
    .map((schema) => schema.name.trim().match(pattern)?.[1])
    .filter((v): v is string => Boolean(v))
    .map(Number);
  const nextVersion = usedVersions.length > 0 ? Math.max(...usedVersions) + 1 : 1;
  return `${templateName} Custom v${nextVersion}`;
}

// ── Rule factories ───────────────────────────────────────────

export function buildMandatoryRule(
  columnId: string,
  name: string,
  id: string = crypto.randomUUID()
): SchemaRule {
  return {
    id,
    title: "Mandatory",
    category: "Syntactical",
    fieldCount: 1,
    ruleKey: `${name} - mandatory`,
    columnIds: [columnId],
    formula: `[${name}] must not be null or empty`,
    description: "Values in this field cannot be null or empty.",
    hasConflict: false,
    criticality: "error",
    backendRuleType: "mandatory",
    dqType: "Mandatory",
    presetId: "mandatory",
    parameters: {},
  };
}

export function buildNoDuplicatesRule(
  columnId: string,
  name: string,
  id: string = crypto.randomUUID()
): SchemaRule {
  return {
    id,
    title: "No Duplicate Values",
    category: "Syntactical",
    fieldCount: 1,
    ruleKey: `${name} - unique`,
    columnIds: [columnId],
    formula: `[${name}] must be a unique value`,
    description: "",
    hasConflict: false,
    criticality: "error",
    backendRuleType: "column_unique",
    dqType: "Duplicates",
    presetId: "column_unique",
    parameters: {},
  };
}

export function isMandatoryRule(r: { dqType?: string; backendRuleType?: string }): boolean {
  return r.dqType === "Mandatory" || r.backendRuleType === "mandatory";
}

export function isDuplicatesRule(r: { dqType?: string; backendRuleType?: string }): boolean {
  return r.dqType === "Duplicates" || r.backendRuleType === "column_unique";
}

/**
 * Synthesizes Mandatory/No-Duplicates rules for columns whose flag is set but have no
 * backing rule yet (e.g. upload inference set the flag without creating the rule).
 */
export function reconcileMissingFlagRules(
  columns: Pick<SchemaColumn, "fieldId" | "name" | "mandatory" | "unique">[],
  rules: SchemaRule[]
): SchemaRule[] {
  const additions: SchemaRule[] = [];
  for (const col of columns) {
    const columnRules = rules.filter(
      (r) => r.columnIds.includes(col.fieldId) && (r.scope ?? "column") === "column"
    );
    if (col.mandatory && !columnRules.some(isMandatoryRule)) {
      additions.push(buildMandatoryRule(col.fieldId, col.name));
    }
    if (col.unique && !columnRules.some(isDuplicatesRule)) {
      additions.push(buildNoDuplicatesRule(col.fieldId, col.name));
    }
  }
  return additions;
}

// ── Auto data-type-check rules (valid_number / valid_integer / valid_date / valid_boolean) ───

// Must match FE_TO_API_TYPE in schemaBuilderApi.ts.
function targetAutoTypeCheckTemplate(
  dataType: SchemaDataType
): "valid_number" | "valid_integer" | "valid_date" | "valid_boolean" | null {
  if (dataType === "Decimal") return "valid_number";
  if (dataType === "Integer") return "valid_integer";
  if (dataType === "Date & Time") return "valid_date";
  if (dataType === "Boolean") return "valid_boolean";
  return null;
}

export function isAutoTypeCheckRule(r: { backendRuleType?: string }): boolean {
  return (
    r.backendRuleType === "valid_number" ||
    r.backendRuleType === "valid_integer" ||
    r.backendRuleType === "valid_date" ||
    r.backendRuleType === "valid_boolean"
  );
}

export function booleanTokensFromFormatting(formatting?: string | null): string[] {
  const raw = (formatting ?? "").trim() || getDefaultFormattingForDataType("Boolean");
  const tokens = raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  return tokens.length > 0 ? tokens : ["1", "0"];
}

export function buildAutoTypeCheckRule(
  columnId: string,
  name: string,
  dataType: SchemaDataType,
  id: string = `__preview_${crypto.randomUUID()}`,
  formatting?: string | null
): SchemaRule | null {
  const template = targetAutoTypeCheckTemplate(dataType);
  if (!template) return null;
  const meta = {
    valid_number: { label: "number", title: "Valid Number", check: "be a valid number" },
    valid_integer: { label: "integer", title: "Valid Integer", check: "be a whole number" },
    valid_date: { label: "date", title: "Valid Date", check: "be a valid date" },
    valid_boolean: { label: "boolean", title: "Valid Boolean", check: "be a valid boolean" },
  }[template];
  const booleanTokens =
    template === "valid_boolean" ? booleanTokensFromFormatting(formatting) : null;
  const formula = booleanTokens
    ? `[${name}] must be one of: ${booleanTokens.join(", ")}`
    : `[${name}] must ${meta.check}`;
  const description = booleanTokens
    ? `Column '${name}' must be one of: ${booleanTokens.join(", ")}`
    : "Created automatically from this column's data type.";
  return {
    id,
    title: meta.title,
    category: "Syntactical",
    fieldCount: 1,
    // Matches the backend's default_rule_name() for the auto type-check templates.
    ruleKey: `${name} - ${meta.label}`,
    columnIds: [columnId],
    formula,
    description,
    hasConflict: false,
    criticality: "error",
    backendRuleType: template,
    dqType: "Data Type",
    presetId: template,
    parameters: booleanTokens ? { values: booleanTokens } : {},
  };
}

/** Adds/removes local preview rules so columns' valid_number/valid_date rules
 * match their current data type; the backend creates the real rule on save. */
export function reconcileDataTypeCheckRules(
  columns: (Pick<SchemaColumn, "fieldId" | "name" | "dataType"> & {
    formatting?: string | null;
  })[],
  rules: SchemaRule[]
): SchemaRule[] {
  const columnByFieldId = new Map(columns.map((c) => [c.fieldId, c]));
  const targetByFieldId = new Map(
    columns.map((c) => [c.fieldId, targetAutoTypeCheckTemplate(c.dataType)])
  );
  const kept = rules.filter((r) => {
    if (!isAutoTypeCheckRule(r)) return true;
    const fieldId = r.columnIds[0];
    return fieldId !== undefined && targetByFieldId.get(fieldId) === r.backendRuleType;
  });
  const refreshed = kept.map((r) => {
    if (r.backendRuleType !== "valid_boolean") return r;
    const fieldId = r.columnIds[0];
    const col = fieldId ? columnByFieldId.get(fieldId) : undefined;
    if (!col) return r;
    const next = buildAutoTypeCheckRule(col.fieldId, col.name, col.dataType, r.id, col.formatting);
    return next ?? r;
  });
  const additions: SchemaRule[] = [];
  for (const col of columns) {
    const target = targetByFieldId.get(col.fieldId);
    if (!target) continue;
    const alreadyCorrect = refreshed.some(
      (r) => r.columnIds.includes(col.fieldId) && r.backendRuleType === target
    );
    if (!alreadyCorrect) {
      const rule = buildAutoTypeCheckRule(
        col.fieldId,
        col.name,
        col.dataType,
        undefined,
        col.formatting
      );
      if (rule) additions.push(rule);
    }
  }
  return [...refreshed, ...additions];
}

// ── Column helpers ────────────────────────────────────────────

export function getDefaultExampleForDataType(dataType: string, fallback?: string): string {
  switch (dataType) {
    case "Boolean":
      return "1";
    case "Decimal":
      return "1204.49";
    case "Date & Time":
      return "03/01/2026";
    case "Integer":
      return "12";
    case "General":
      return "AAAABB12345";
    default:
      return fallback ?? "";
  }
}

/** Must match one of ColumnEditorDrawer's FormattingSelect options for that type. */
export function getDefaultFormattingForDataType(dataType: string): string {
  switch (dataType) {
    case "Boolean":
      return "1, 0";
    case "Date & Time":
      return "01/03/2024";
    default:
      return "---";
  }
}

export type ColumnOption = { name: string; fieldId: string };

export function toColumnOptions(cols: SchemaColumn[]): ColumnOption[] {
  return cols.map((c) => ({ name: c.name, fieldId: c.fieldId }));
}

export function columnNamesById(cols: SchemaColumn[]): Record<string, string> {
  return Object.fromEntries(cols.map((c) => [c.fieldId, c.name]));
}

export function columnIdsByName(cols: SchemaColumn[]): Record<string, string> {
  return Object.fromEntries(cols.map((c) => [c.name, c.fieldId]));
}

// Parameter keys that hold a column *name* to resolve, vs config values (reference list id,
// tolerance, min/max, case sensitivity) that happen to share the object but aren't columns.
const NON_COLUMN_PARAM_KEYS = new Set([
  "reference_list_id",
  "tolerance",
  "min",
  "max",
  "case_sensitive",
]);

// True if a cross-column rule's stored column-name parameters (x_column, components[], etc.)
// no longer match a current column, e.g. that column was renamed/deleted after the rule was saved.
export function ruleReferencesUnresolvedColumn(
  rule: Pick<SchemaRule, "parameters">,
  columnsByName: Record<string, string>
): boolean {
  const params = rule.parameters ?? {};
  return Object.entries(params).some(([key, val]) => {
    if (NON_COLUMN_PARAM_KEYS.has(key)) return false;
    if (Array.isArray(val)) {
      return val.some((v) => typeof v === "string" && v.length > 0 && !columnsByName[v]);
    }
    if (typeof val === "string" && val.length > 0 && (key.endsWith("_column") || key === "col")) {
      return !columnsByName[val];
    }
    return false;
  });
}

// Rewrites column-name parameters equal to `oldName` so a rename doesn't strand a
// cross-column rule's reference and flag it as a conflict.
export function renameColumnInRuleParameters(
  rule: SchemaRule,
  oldName: string,
  newName: string
): SchemaRule {
  const params = rule.parameters;
  if (!params) return rule;
  let changed = false;
  const nextParams: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(params)) {
    if (NON_COLUMN_PARAM_KEYS.has(key)) {
      nextParams[key] = val;
    } else if (Array.isArray(val)) {
      const nextArr = val.map((v) => (v === oldName ? newName : v));
      if (nextArr.some((v, i) => v !== val[i])) changed = true;
      nextParams[key] = nextArr;
    } else if (
      typeof val === "string" &&
      (key.endsWith("_column") || key === "col") &&
      val === oldName
    ) {
      nextParams[key] = newName;
      changed = true;
    } else {
      nextParams[key] = val;
    }
  }
  return changed ? { ...rule, parameters: nextParams } : rule;
}

// Substitutes `{param_key}` placeholders in the preset's expression template. Re-run when a
// referenced column/list is renamed so a rule card's displayed formula doesn't go stale.
export function renderRuleFormula(
  preset: Pick<SchemaRulePreset, "expression" | "description">,
  parameters: Record<string, unknown>,
  columnName: string,
  referenceListNameById: Record<string, string> = {}
): string {
  let formula = preset.expression?.split("{col}").join(columnName) ?? preset.description;
  for (const [key, val] of Object.entries(parameters)) {
    if (key === "reference_list_id" && typeof val === "string") {
      formula = formula
        .split(`{${key}}`)
        .join(referenceListNameById[val] ?? "reference list not found");
    } else if (typeof val === "string") {
      formula = formula.split(`{${key}}`).join(val);
    } else if (typeof val === "number") {
      formula = formula.split(`{${key}}`).join(String(val));
    } else if (Array.isArray(val) && val.length > 0) {
      const joined = (val as string[]).map((v) => `[${v}]`).join(", ");
      formula = formula.split(`{${key}}`).join(joined);
    }
  }
  if (/\{[a-z_]+\}/.test(formula)) return preset.description;
  return formula;
}

// ── Provider schema assignments (PSA) ─────────────────────────

/**
 * Build a reverse index: schema_id → providers that have it actively assigned (PSA).
 * Needed because provider list responses carry assigned_schemas, while schema cards
 * need the opposite lookup for pills and the Active for Provider tab.
 */
export function buildSchemaToProvidersMap(
  providers: Pick<
    ProviderOut,
    "provider_id" | "provider_short_code" | "provider_name" | "assigned_schemas"
  >[]
): Map<string, SchemaAssignedProvider[]> {
  const map = new Map<string, SchemaAssignedProvider[]>();
  for (const provider of providers) {
    const entry: SchemaAssignedProvider = {
      providerId: provider.provider_id,
      shortCode: provider.provider_short_code,
      name: provider.provider_name,
    };
    for (const assignment of provider.assigned_schemas) {
      const list = map.get(assignment.schema_id) ?? [];
      list.push(entry);
      map.set(assignment.schema_id, list);
    }
  }
  return map;
}

/**
 * Providers to show as pills on a schema card.
 * - No provider selected: all PSA assignments for the schema.
 * - Provider selected: only that provider, and only if assigned.
 */
export function getProviderPillsForSchema(
  schemaId: string,
  schemaToProviders: Map<string, SchemaAssignedProvider[]>,
  selectedProviderId: string | null
): SchemaAssignedProvider[] {
  const assigned = schemaToProviders.get(schemaId) ?? [];
  if (!selectedProviderId) return assigned;
  return assigned.filter((p) => p.providerId === selectedProviderId);
}
